import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@ngneat/transloco';
import { CustomSnackbarComponent } from '../custom-snackbar/custom-snackbar.component';
import { toErrorTrace } from '../utils/http-error.util';

export interface SnackbarConfig {
  message?: string;
  errorId?: string;
  icon?: string;
  type?: 'success' | 'error' | 'info';
  duration?: number;
  horizontalPosition?: 'start' | 'center' | 'end' | 'left' | 'right';
  verticalPosition?: 'top' | 'bottom';
  trace?: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class SnackbarService {
  private snackBar = inject(MatSnackBar);
  private translocoService = inject(TranslocoService);

  show(config: SnackbarConfig): void {
    const {
      message,
      errorId,
      icon = 'heroicons_solid:check-circle',
      type = 'success',
      duration = 5000,
      horizontalPosition = 'center',
      verticalPosition = 'bottom',
      trace,
    } = config;

    let displayMessage = message;

    if (errorId) {
      displayMessage = this.translocoService.translate(`errors.${errorId}`);
    }

    if (!displayMessage) {
      return;
    }

    const snackbarDuration = type === 'error' ? 0 : duration;

    this.snackBar.openFromComponent(CustomSnackbarComponent, {
      data: {
        message: displayMessage,
        icon: this.getIconForType(type, errorId),
        type,
        trace,
      },
      duration: snackbarDuration,
      horizontalPosition,
      verticalPosition,
    });
  }

  showSuccess(message: string, config?: Partial<SnackbarConfig>): void {
    this.show({
      message: this.translocoService.translate(message),
      icon: 'heroicons_solid:check-circle',
      type: 'success',
      ...config,
    });
  }

  showError(message: string, config?: Partial<SnackbarConfig>): void {
    this.show({
      message: this.translocoService.translate(message),
      icon: 'heroicons_solid:exclamation-triangle',
      type: 'error',
      ...config,
    });
  }

  showHttpError(err: unknown, config?: Partial<SnackbarConfig>): void {
    let errorId =
      err instanceof HttpErrorResponse && err.error && typeof err.error === 'object'
        ? (err.error as { errorId?: string }).errorId
        : undefined;

    if (!errorId && err instanceof HttpErrorResponse && err.status === 0) {
      errorId = 'network-error';
    }

    if (err != null) {
      console.error(errorId ?? 'GenericError', err);
    }
    this.showErrorFromId(errorId, err, config);
  }

  showErrorFromId(
    errorId: string | null | undefined,
    trace?: unknown,
    config?: Partial<SnackbarConfig>
  ): void {
    const resolvedErrorId = errorId || 'GenericError';
    const normalizedTrace =
      trace !== undefined && trace !== null ? toErrorTrace(trace) : undefined;

    if (normalizedTrace && !normalizedTrace.errorId) {
      normalizedTrace.errorId = resolvedErrorId;
    }

    this.show({
      errorId: resolvedErrorId,
      icon: 'heroicons_solid:exclamation-triangle',
      type: 'error',
      trace: normalizedTrace,
      ...config,
    });
  }

  showInfo(message: string, config?: Partial<SnackbarConfig>): void {
    this.show({
      message: this.translocoService.translate(message),
      icon: 'heroicons_solid:information-circle',
      type: 'info',
      ...config,
    });
  }

  showRawMessage(message: string, config?: Partial<SnackbarConfig>): void {
    this.show({
      message,
      icon: 'heroicons_solid:information-circle',
      type: 'info',
      ...config,
    });
  }

  private getIconForType(type: 'success' | 'error' | 'info', errorId?: string): string {
    if (errorId) {
      return 'heroicons_solid:exclamation-triangle';
    }

    switch (type) {
      case 'success':
        return 'heroicons_solid:check-circle';
      case 'error':
        return 'heroicons_solid:exclamation-triangle';
      case 'info':
        return 'heroicons_solid:information-circle';
      default:
        return 'heroicons_solid:check-circle';
    }
  }
}
